import { useSelector, useDispatch } from "react-redux";
import { setUser, initialState } from "store/auth/userSlice";
import {
  apiGoogleSignIn,
  apiGoogleSignUp,
  apiSignIn,
  apiSignOut,
  apiSignUp,
} from "services/AuthService";
import { onSignInSuccess, onSignOutSuccess } from "store/auth/sessionSlice";
import { clearStudentSession } from "utils/studentSession";
import appConfig from "configs/app.config";
import { REDIRECT_URL_KEY } from "constants/app.constant";
import { useNavigate } from "react-router-dom";
import useQuery from "./useQuery";
import { useAlert } from "react-alert";

function useAuth() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const query = useQuery();

  const alert = useAlert();

  const { token, signedIn } = useSelector((state) => state.auth.session);

  const signIn = async (values, options) => {
    try {
      console.log(values);
      const resp = await apiSignIn(values);
      console.log(resp);
      if (resp.data) {
        const { token, user, config, scope, verified } = resp.data;
        dispatch(onSignInSuccess({ payload: token }));
        localStorage.setItem("token", token);

        dispatch(
          setUser({
            avatar: "",
            username: user,
            authority: [scope],
            email: user,
            config: config,
            verified: verified ?? true,
          }),
        );

        const redirectUrl = query.get(REDIRECT_URL_KEY);
        if (!options?.skipRedirect) {
          if (verified === false) {
            localStorage.setItem("purpose", "signin");
            navigate("/app/otp?purpose=signin");
          } else {
            navigate(
              redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath,
            );
          }
        }
        return {
          status: "success",
          message: "",
          config,
          verified,
        };
      }
    } catch (errors) {
      console.log(errors);
      // Shown inline by the sign-in forms (there is no toast for sign-in), so keep it readable.
      const data = errors?.response?.data;
      return {
        status: "failed",
        message:
          data?.message ||
          data?.error ||
          (errors?.response ? "Sign in failed. Check your details and try again." : "Can't reach the server. Check your connection and try again."),
      };
    }
  };

  const signUp = async (values, options = {}) => {
    try {
      values["failedOnExist"] = options?.failedOnExist;
      const resp = await apiSignUp(values, alert);
      if (resp.data) {
        localStorage.removeItem("tsv_ref_code");
        const { token, user, config, scope, verified } = resp.data;
        dispatch(onSignInSuccess({ payload: token }));

        dispatch(
          setUser({
            avatar: "",
            username: user,
            authority: [scope],
            email: user,
            config: config,
            verified: verified ?? true,
          }),
        );

        const redirectUrl = query.get(REDIRECT_URL_KEY);
        if (!options?.skipRedirect) {
          if (verified === false) {
            localStorage.setItem("purpose", "signup");
            navigate("/app/otp?purpose=signup");
          } else {
            navigate(
              redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath,
            );
          }
        }
        return {
          status: "success",
          message: "",
          config,
          verified,
        };
      }
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const signUpWithGoogle = async (credential, options = {}) => {
    try {
      const resp = await apiGoogleSignUp(
        {
          credential,
          failedOnExist: options?.failedOnExist,
          metaFbc: options?.metaFbc || null,
          metaFbp: options?.metaFbp || null,
          metaFbclid: options?.metaFbclid || null,
          metaClientIp: options?.metaClientIp || null,
        },
        alert,
      );
      if (resp.data) {
        localStorage.removeItem("tsv_ref_code");
        const { token, user, config, scope, verified } = resp.data;
        dispatch(onSignInSuccess({ payload: token }));
        dispatch(
          setUser({
            avatar: "",
            username: user,
            authority: [scope],
            email: user,
            config: config,
            verified: verified ?? true,
          }),
        );

        const redirectUrl = query.get(REDIRECT_URL_KEY);
        if (!options?.skipRedirect) {
          if (verified === false) {
            localStorage.setItem("purpose", "signin");

            navigate("/app/otp?purpose=signup");
          } else {
            navigate(
              redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath,
            );
          }
        }
        return { status: "success", message: "", config, verified };
      }
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const signInWithGoogle = async (credential, options) => {
    try {
      const resp = await apiGoogleSignIn({ credential }, alert);
      if (resp.data) {
        const { token, user, config, scope, verified } = resp.data;
        dispatch(onSignInSuccess({ payload: token }));
        dispatch(
          setUser({
            avatar: "",
            username: user,
            authority: [scope],
            email: user,
            config: config,
            verified: verified ?? true,
          }),
        );

        const redirectUrl = query.get(REDIRECT_URL_KEY);

        if (!options?.skipRedirect) {
          if (verified === false) {
            localStorage.setItem("purpose", "signin");

            navigate("/app/otp?purpose=signin");
          } else {
            navigate(
              redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath,
            );
          }
        }
        return { status: "success", message: "", config, verified };
      }
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const handleSignOut = () => {
    const AUTO_OTP_KEY = "autoOtpSent";
    const OTP_COOLDOWN_KEY = "otpCooldownUntil";
    const OTP_DESTINATION_KEY = "otpDestination";
    localStorage.removeItem(AUTO_OTP_KEY);
    localStorage.removeItem(OTP_COOLDOWN_KEY);
    localStorage.removeItem(OTP_DESTINATION_KEY);
    localStorage.removeItem("viewId");
    localStorage.removeItem("purpose");
    clearStudentSession();

    dispatch(onSignOutSuccess());
    dispatch(setUser(initialState));
    navigate(appConfig.publicRoute);
  };

  const signOut = async () => {
    await apiSignOut();
    handleSignOut();
  };

  return {
    authenticated: token && signedIn,
    signIn,
    signUp,
    signOut,
    signUpWithGoogle,
    signInWithGoogle,
  };
}

export default useAuth;
